import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAllMeetings,
  selectMeetingsLoading,
  setMeetings,
  addMeeting,
  updateMeeting,
  removeMeeting,
  appendMeetings,
} from "../store/slices/meetingsSlice";
import {
  getMeetings,
  createMeeting,
  updateMeeting as apiUpdateMeeting,
  deleteMeeting as apiDeleteMeeting,
} from "../services/api";
import MeetingRow from "../components/meetings/MeetingRow";
import MeetingCard from "../components/meetings/MeetingCard";
import MeetingFormModal from "../components/meetings/MeetingFormModal";
import { ROLES } from "../config/roles";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useUiLanguage } from "../components/profile/coaching-personality/useUiLanguage";
import i18n from "../i18n";
import { getStaticTranslation } from "../translations/registerStaticTranslations";

// Canonical My-Meetings filter pills (design 5d): Upcoming · Past — pills, not
// tabs (D16 language rule). "All" is dropped to match the frame.
const filterPills = [
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
];

// Date-group label for the list headers (5d: TODAY / TOMORROW / a dated label).
const dayGroupLabel = (date) => {
  const d = new Date(date);
  const today = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diffDays = Math.round((startOf(d) - startOf(today)) / 86400000);
  if (diffDays === 0) return "TODAY";
  if (diffDays === 1) return "TOMORROW";
  if (diffDays === -1) return "YESTERDAY";
  return d
    .toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })
    .toUpperCase();
};

const Meetings = () => {
  const dispatch = useDispatch();
  // QAV5-502: window.confirm() renders a native browser dialog, outside the
  // DOM, so the app's translation scanner (which walks document.body text
  // nodes) can never reach it. Look the string up through the same static
  // bundle the scanner itself uses (meetings.json already carries this exact
  // sentence in all 16 locales) before handing it to the native dialog.
  const currentLanguage = useUiLanguage();
  const tr = (s) => getStaticTranslation(i18n, s, currentLanguage) || s;
  const meetings = useSelector(selectAllMeetings);
  const isLoading = useSelector(selectMeetingsLoading);
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("upcoming");
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const selectedMeeting =
    meetings.find((m) => m.id === selectedMeetingId) || null;

  // Fetch meetings
  const fetchMeetings = async (pageToLoad = 1, append = false) => {
    if (append) dispatch({ type: "meetings/setLoading", payload: true });
    const res = await getMeetings(pageToLoad);
    if (res.success) {
      if (append) {
        dispatch(appendMeetings(res.data));
      } else {
        dispatch(setMeetings(res.data));
      }
      setHasNext(Boolean(res.next));
    } else {
      toast.error("Failed to fetch meetings");
      dispatch({ type: "meetings/setError", payload: res.error });
    }
    dispatch({ type: "meetings/setLoading", payload: false });
  };

  useEffect(() => {
    fetchMeetings(1, false);
  }, []);

  const handleUpsertMeeting = async (payload) => {
    const res = await createMeeting(payload);
    if (res.success) {
      dispatch(addMeeting(res.data));
      setModalOpen(false);
      toast.success("Meeting created");
    } else {
      toast.error(res.error || "Failed to create meeting");
    }
  };

  const handleEditMeeting = (meeting) => {
    setEditingMeeting(meeting);
    setModalOpen(true);
  };

  const handleSaveEdit = async (payload) => {
    const res = await apiUpdateMeeting(editingMeeting.id, payload);
    if (res.success) {
      dispatch(updateMeeting(res.data));
      toast.success("Meeting updated");
      setModalOpen(false);
      setEditingMeeting(null);
    } else {
      toast.error(res.error || "Failed to update meeting");
    }
  };

  const handleCancelMeeting = async (meeting) => {
    if (!window.confirm(tr("Cancel this meeting?"))) return;
    const res = await apiDeleteMeeting(meeting.id);
    if (res.success) {
      dispatch(removeMeeting(meeting.id));
      toast.success("Meeting cancelled");
    } else {
      toast.error(res.error || "Failed to cancel meeting");
    }
  };

  // Open the meeting in its Zoom room — host uses start_url, guest join_url
  // (same rule the detail pane applies). Used by the row's inline "Join".
  const handleJoinMeeting = (meeting) => {
    const isHost = meeting.schedule_by?.email === user?.email;
    const url = isHost ? meeting.start_url : meeting.join_url;
    if (url) window.open(url, "_blank");
  };

  // Filter by pill, then sort: Upcoming ascending (soonest first), Past
  // descending (most recent first) — the natural reading order for each.
  const now = new Date();
  const isPastMeeting = (m) => new Date(m.schedule_date) < now;
  const filteredMeetings = meetings
    .filter((m) => (activeTab === "past" ? isPastMeeting(m) : !isPastMeeting(m)))
    .sort((a, b) => {
      const da = new Date(a.schedule_date).getTime();
      const db = new Date(b.schedule_date).getTime();
      return activeTab === "past" ? db - da : da - db;
    });

  // Group the filtered rows under their date label (5d: TODAY / TOMORROW / …),
  // preserving the sorted order within each group.
  const groupedMeetings = filteredMeetings.reduce((groups, m) => {
    const label = dayGroupLabel(m.schedule_date);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(m);
    else groups.push({ label, items: [m] });
    return groups;
  }, []);

  return (
    <div className="pr-6 mx-auto bg-[#F8F9FB]">
      <div className="flex h-[calc(100vh-80px)] gap-6">
        {/* Sidebar */}
        <div className="w-80 px-2 py-3 flex flex-col border-r bg-white  ">
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-4">
              <svg
              className="cursor-pointer"
                onClick={() => navigate(-1)}
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.8332 6.99935H1.1665M1.1665 6.99935L6.99984 12.8327M1.1665 6.99935L6.99984 1.16602"
                  stroke="#1E1E1E"
                  stroke-width="1.875"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              My Meetings
            </h2>
            {(user?.roles || []).some((r) =>
              [ROLES.ClubAdmin, ROLES.Staff].includes(r)
            ) && (
              <button
                type="button"
                onClick={() => {
                  setModalOpen(true);
                  setEditingMeeting(null);
                }}
                className="w-full font-light flex items-center justify-center gap-2 mb-4 px-4 py-3 text-sm text-[#F5F5F5] bg-[#2283FF] rounded-md hover:bg-blue-700"
              >
                <svg
                  width="15"
                  height="16"
                  viewBox="0 0 15 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.50016 5.33398V10.6673M4.8335 8.00065H10.1668M14.1668 8.00065C14.1668 11.6825 11.1821 14.6673 7.50016 14.6673C3.81826 14.6673 0.833496 11.6825 0.833496 8.00065C0.833496 4.31875 3.81826 1.33398 7.50016 1.33398C11.1821 1.33398 14.1668 4.31875 14.1668 8.00065Z"
                    stroke="#F5F5F5"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                New meeting
              </button>
            )}
            {/* Upcoming · Past filter pills (design 5d) — pills, not tabs. */}
            <div className="mb-3 flex items-center gap-1.5">
              {filterPills.map((pill) => {
                const active = activeTab === pill.key;
                return (
                  <button
                    key={pill.key}
                    type="button"
                    onClick={() => {
                      setActiveTab(pill.key);
                      setSelectedMeetingId(null);
                    }}
                    className="rounded-full text-[12px] font-semibold transition-colors"
                    style={{
                      padding: "5px 12px",
                      background: active ? "#101828" : "#F2F4F7",
                      color: active ? "#FFFFFF" : "#475467",
                    }}
                  >
                    {pill.label}
                  </button>
                );
              })}
            </div>

            <div className="space-y-1 overflow-y-auto max-h-full pr-1">
              {/* Date-grouped rows (5d): TODAY / TOMORROW / dated headers. */}
              {groupedMeetings.map((group) => (
                <div key={group.label} className="mb-1">
                  <span
                    className="mb-1 block px-2"
                    style={{
                      font: "600 9.5px ui-monospace,Menlo,monospace",
                      color: "#98A2B3",
                      letterSpacing: ".06em",
                    }}
                  >
                    {group.label}
                  </span>
                  {group.items.map((m) => (
                    <MeetingRow
                      key={m.id}
                      meeting={m}
                      isSelected={selectedMeetingId === m.id}
                      isPast={activeTab === "past"}
                      currentEmail={user?.email}
                      onClick={() => setSelectedMeetingId(m.id)}
                      onJoin={handleJoinMeeting}
                    />
                  ))}
                </div>
              ))}
              {filteredMeetings.length === 0 && !isLoading && (
                <p className="text-sm text-[#111927] font-medium">
                  {activeTab === "past" ? "No past meetings" : "No upcoming meetings"}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Main Pane */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && <p>Loading…</p>}
          {!isLoading && !selectedMeeting && !isModalOpen && (
            <p className="text-gray-500">
              Select a meeting from the left or create a new one.
            </p>
          )}

          {selectedMeeting && !isModalOpen && (
            <MeetingCard
              meeting={selectedMeeting}
              isHost={selectedMeeting.schedule_by?.email === user?.email}
              onStartJoin={() =>
                window.open(
                  selectedMeeting.schedule_by?.email === user?.email
                    ? selectedMeeting.start_url
                    : selectedMeeting.join_url,
                  "_blank"
                )
              }
              onEdit={() => handleEditMeeting(selectedMeeting)}
              onCancel={() => handleCancelMeeting(selectedMeeting)}
            />
          )}
        </div>
      </div>

      {/* Modal */}
      <MeetingFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingMeeting(null);
        }}
        onSubmit={editingMeeting ? handleSaveEdit : handleUpsertMeeting}
        mode={editingMeeting ? "edit" : "create"}
        initialData={editingMeeting || {}}
      />
    </div>
  );
};

export default Meetings;
